import { useSelector } from 'react-redux';

import { State } from 'reducers';
import { selectUser } from 'entities/user/selectors';
import { useAvailableDashboardsQuery, ExternalDashboardType } from 'generated/graphql';

import { getDashboards, DashboardDefinition } from './dashboardDefinitions';

const selectDashboardDefintions = (state: State) => {
  const user = selectUser(state);
  if (user) {
    return getDashboards(user);
  }
  return [];
};

const useAvailableDashboardTypes = () => {
  const { data: availableDashboards, loading } = useAvailableDashboardsQuery();
  return { availableDashboards: availableDashboards?.dashboards ?? [], loading };
};

export const useAvailableDashboardDefinitions = () => {
  const allDashboardDefinitions = useSelector(selectDashboardDefintions);
  const { availableDashboards, loading } = useAvailableDashboardTypes();

  const dashboardDefinitionsMap = new Map<ExternalDashboardType, DashboardDefinition>(
    allDashboardDefinitions.map((def) => [def.type, def])
  );

  const mergedDefinitions: DashboardDefinition[] = availableDashboards
    .map((dbDashboard) => {
      const staticDef = dashboardDefinitionsMap.get(dbDashboard.type);

      if (!staticDef) return null;

      // Merge DB metadata with static definition
      // DB values override static values when present
      return {
        ...staticDef,
        title: dbDashboard.title ?? staticDef.title,
        text: dbDashboard.description ?? staticDef.text,
        icon: dbDashboard.iconUrl ?? staticDef.icon,
        sortOrder: dbDashboard.sortOrder ?? staticDef.sortOrder,
      };
    })
    .filter((def): def is DashboardDefinition => def !== null)
    .sort((a, b) => {
      // Sort by sortOrder if both have it, otherwise maintain query order
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      if (a.sortOrder !== undefined) return -1;
      if (b.sortOrder !== undefined) return 1;
      return 0;
    });

  return { dashboardDefinitions: mergedDefinitions, loading };
};
